import React, { useState, useRef, useCallback, Dispatch, SetStateAction, useEffect } from "react";
import Dropdown from "react-bootstrap/Dropdown";

import { StateLanguage } from "../../languages/config/StateLanguage";
import { evaluateLegendRequiredIcon } from "../../scripts/validate.legend.script";
import { expressions } from "../../libraries/regular.expressions.library";
import { legendIconValid, legendIconValidInvalidRestart, legendIconValidRequiredRestart, legendIconValidRequired } from "../../utils/legend.data.util";
import { useDropdownKeyboardNav } from "../../hooks/useDropdownKeyboardNav";

import AppLegend from "./Legend";

export interface AppInputAddressAutocompleteProps {
  street: {value: string, place: string, valid: boolean},
  setStreet: Dispatch<SetStateAction<{value: string, place: string, valid: boolean}>>,
  setStreetNumber: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdditional: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdministrativeArea: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCity: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setZipCode: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCountry: Dispatch<SetStateAction<{value: string, valid: boolean}>>
}

/** Debounce delay for autocomplete requests (ms). */
const DEBOUNCE_MS = 300;

/**
 * Only request address_components from Place Details.
 * This keeps the request in the "Basic" billing tier.
 * Requesting all fields (the default) triggers "Contact" + "Atmosphere"
 * tiers and costs significantly more per call.
 */
const DETAILS_FIELDS = ['address_components'];

const AppInputAddressAutocomplete: React.FunctionComponent<AppInputAddressAutocompleteProps> = ({street, setStreet, setStreetNumber, setAdditional, setAdministrativeArea, setCity, setZipCode, setCountry}) => {
  const {lang} = StateLanguage()

  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null)
  const detailsRef = useRef<google.maps.places.PlacesService | null>(null)
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([])

  const selectByIndexRef = useRef((_idx: number) => {})

  const { highlightedIndex, handleKeyDown: kbHandleKeyDown, getOptionId, activeDescendant, listboxId } =
    useDropdownKeyboardNav({
      itemCount: predictions.length,
      onSelect: (idx) => selectByIndexRef.current(idx),
      isOpen: predictions.length > 0,
      onClose: () => setPredictions([]),
      idPrefix: 'address',
    })

  /** Create a fresh session token. One token per autocomplete "session". */
  const newSessionToken = useCallback(() => {
    if (window.google) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken()
    }
  }, [])

  const handleChangeStreet = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let input: string = event.target.value
    setStreet({...street, value: input})

    // Debounce autocomplete requests
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!serviceRef.current || !input) {
      setPredictions([])
      return
    }

    debounceRef.current = setTimeout(() => {
      // Create session token on first request of a new session
      if (!sessionTokenRef.current) newSessionToken()

      serviceRef.current!.getPlacePredictions(
        {
          input,
          componentRestrictions: {country: 'ch'},
          sessionToken: sessionTokenRef.current!,
        },
        (res) => { setPredictions(res || []) }
      )
    }, DEBOUNCE_MS)
  }

  const handleSelectStreet = (item: google.maps.places.AutocompletePrediction) => {
    setPredictions([])

    if (detailsRef.current) {
      detailsRef.current.getDetails(
        {
          placeId: item.place_id,
          fields: DETAILS_FIELDS,
          sessionToken: sessionTokenRef.current!,
        },
        (place, status) => {
          // Session is complete — token is consumed. Generate a new one for the next session.
          sessionTokenRef.current = null

          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            let components: google.maps.GeocoderAddressComponent[] = place.address_components || []

            const getAddressComponent = (type: string): { valid: boolean; value: string } => {
              let value: string = components.find((component) => component.types.includes(type))?.long_name || ""
              return {valid: value.length > 0, value: value}
            }

            const getAdditionalComponent = (): { valid: boolean; value: string } => {
              let types: string[] = ['sublocality_level_1', 'sublocality', 'neighborhood', 'premise']
              let result = types.map(getAddressComponent).find(component => component.valid && component.value.trim() !== "")
              return result || {valid: false, value: ''}
            }

            const getCountryComponent = (): { valid: boolean; value: string } => {
              let value: string = components.find((component) => component.types.includes('country'))?.short_name || ""
              return {valid: value.length > 0, value: value}
            }

            setStreet({valid: true, place: item.place_id, value: getAddressComponent('route').valid ? getAddressComponent('route').value : street.value})
            setStreetNumber(getAddressComponent('street_number'))
            setAdditional(getAdditionalComponent())
            setAdministrativeArea(getAddressComponent('administrative_area_level_1'))
            setCity(getAddressComponent('locality'))
            setZipCode(getAddressComponent('postal_code'))
            setCountry(getCountryComponent())

            if (getAddressComponent('street_number').valid) {
              legendIconValidRequired('input-street-number', 'legend-street-number-valid', 'legend-street-number-required')
            } else{
              legendIconValidRequiredRestart('input-street-number', 'legend-street-number-valid', 'legend-street-number-required')
            }

            if (getAdditionalComponent().valid) {
              legendIconValid('input-additional', 'legend-additional-valid')
            } else{
              legendIconValidInvalidRestart('input-additional', 'legend-additional-valid')
            }

            if (getAddressComponent('administrative_area_level_1').valid) {
              legendIconValidRequired('input-administrative-area', 'legend-administrative-area-valid', 'legend-administrative-area-required')
            } else{
              legendIconValidRequiredRestart('input-administrative-area', 'legend-administrative-area-valid', 'legend-administrative-area-required')
            }

            if (getAddressComponent('locality').valid) {
              legendIconValidRequired('input-city', 'legend-city-valid', 'legend-city-required')
            } else{
              legendIconValidRequiredRestart('input-city', 'legend-city-valid', 'legend-city-required')
            }

            if (getAddressComponent('postal_code').valid) {
              legendIconValidRequired('input-zip-code', 'legend-zip-code-valid', 'legend-zip-code-required')
            } else{
              legendIconValidRequiredRestart('input-zip-code', 'legend-zip-code-valid', 'legend-zip-code-required')
            }

            if (getCountryComponent().valid) {
              legendIconValid('input-country', 'legend-country-required')
            } else{
              legendIconValidInvalidRestart('input-country', 'legend-country-required')
            }
          }
        }
      )
    }
  }

  // Keep the ref up to date so keyboard selection uses the latest predictions
  selectByIndexRef.current = (idx: number) => {
    if (idx >= 0 && idx < predictions.length) {
      handleSelectStreet(predictions[idx])
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    kbHandleKeyDown(e)
  }

  const validateStreet = () => {
    evaluateLegendRequiredIcon(expressions.street, street, setStreet, 'input-street', 'legend-street-valid', 'legend-street-required')
  }

  useEffect(() => {
    if (window.google && !serviceRef.current && !detailsRef.current) {
      serviceRef.current = new window.google.maps.places.AutocompleteService()
      detailsRef.current = new window.google.maps.places.PlacesService(document.createElement("div"))
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
          <span className="selection">
            <input id="input-street" className="form-control" type="text" autoComplete="off" name="street" role="combobox" aria-autocomplete="list" aria-controls={listboxId} aria-activedescendant={activeDescendant} aria-expanded={predictions.length > 0} value={street.value} onChange={handleChangeStreet} onKeyDown={handleInputKeyDown} onKeyUp={validateStreet} onBlur={validateStreet} />
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-results">
              <ul className="select2-results__options" id={listboxId} role="listbox">
                { predictions.length > 0
                  ?
                  <>
                    { predictions.map (( (item, index) => { return (
                      <li key={index} id={getOptionId(index)} className={`select2-results__option select2-results__option--selectable ${item.place_id === street.place ? "select2-results__option--selected" : ""} ${index === highlightedIndex ? "select2-results__option--highlighted" : ""}`} role="option" aria-selected={item.place_id === street.place}>
                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleSelectStreet(item)}>{item.description}</Dropdown.Item>
                      </li>
                    )}))}
                  </>
                  :
                  <li className="select2-results__option select2-results__message" role="alert">{lang.labels.search}</li>
                }
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      <AppLegend attribute={{check: street.valid, name: "street", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
    </>
  );
};

export default AppInputAddressAutocomplete;
